import { useEffect, useState } from "react";
import { FaTimes } from "react-icons/fa";
import { useRecoilValue, useSetRecoilState } from "recoil";
import styled, { keyframes } from "styled-components";
import { analytics } from "MVVM/Analytics";
import banner from "MVVM/Assets/Homepage/sour-hex-card.webp";
import { AllstarUser, StripePaystationState } from "MVVM/State";
import {
  CREATOR_CARD_LOOKUP,
  cardPurchasedUrl,
  ownsCard,
} from "MVVM/Utilities/Studio";
import { CONTENT_MAX_WIDTH } from "./styledComponents";

const SOUR_HEX_CARD_ID = "6ae0ccbf676bc44bf99b6409";
const DISMISSED_KEY = "sourHexAnnouncementDismissed";
const promotionName = "sour-hex-card";
const staticRoot = `${process.env.REACT_APP_S3_STATIC}/static`;
const SHOWCASE_VIDEO = `${staticRoot}/cards/thumbnails/specialfx/mp4/sourhex_thumbnail_v2.mp4`;
const SHOWCASE_POSTER = `${staticRoot}/cards/thumbnails/specialfx/webp/sourhex_thumbnail_v2.webp`;

export const SourHexAnnouncement = () => {
  const allstarUser = useRecoilValue(AllstarUser);
  const setPaystation = useSetRecoilState(StripePaystationState);
  const [dismissed, setDismissed] = useState(() => {
    try {
      return localStorage.getItem(DISMISSED_KEY) === "1";
    } catch {
      return false;
    }
  });
  const owned = ownsCard(allstarUser?.user, SOUR_HEX_CARD_ID);
  const hidden = dismissed || owned;

  useEffect(() => {
    if (!hidden) {
      analytics.track("Homepage Announcement - View", { promotionName });
    }
  }, [hidden]);

  if (hidden) return null;

  const dismiss = () => {
    try {
      localStorage.setItem(DISMISSED_KEY, "1");
    } catch {
      // The banner can still be dismissed when storage is unavailable.
    }
    setDismissed(true);
    analytics.track("Homepage Announcement - Dismiss", { promotionName });
  };

  const buy = () => {
    analytics.track("Homepage Announcement - Click", { promotionName });
    setPaystation({
      isModalOpen: true,
      type: "purchase",
      lookUp: CREATOR_CARD_LOOKUP,
      cardId: SOUR_HEX_CARD_ID,
      returnUrl: cardPurchasedUrl(SOUR_HEX_CARD_ID),
      pendingEmail: false,
      shouldRefreshMtx: false,
    });
  };

  return (
    <Banner>
      <BuyButton
        type="button"
        onClick={buy}
        aria-label="Get the Sour Hex creator card for $1.99"
      >
        <img src={banner} alt="" width={2000} height={683} />
        <Showcase>
          <video
            src={SHOWCASE_VIDEO}
            poster={SHOWCASE_POSTER}
            autoPlay
            muted
            loop
            playsInline
            aria-hidden="true"
          />
          <InAction>
            <i aria-hidden="true" />
            In action
          </InAction>
        </Showcase>
      </BuyButton>
      <Dismiss
        type="button"
        onClick={dismiss}
        aria-label="Dismiss Sour Hex announcement"
      >
        <FaTimes aria-hidden="true" size={13} />
      </Dismiss>
    </Banner>
  );
};

const Banner = styled.section`
  align-self: center;
  border: 1px solid ${({ theme }) => theme.colors.chalkAlpha20};
  border-radius: 12px;
  box-sizing: border-box;
  margin: 0 auto;
  max-width: ${CONTENT_MAX_WIDTH};
  overflow: hidden;
  position: relative;
  width: 100%;
`;

const pulse = keyframes`
  50% { opacity: 0.35; }
`;

const BuyButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  display: block;
  padding: 0;
  position: relative;
  width: 100%;

  > img {
    display: block;
    height: auto;
    width: 100%;
  }

  &:hover > img {
    filter: brightness(1.08);
  }

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.colors.ice};
    outline-offset: -4px;
  }
`;

// Sits over the card box in the banner art, so it is placed in % of the image.
const Showcase = styled.div`
  aspect-ratio: 16 / 9;
  background: #000;
  border: 2px solid rgba(168, 224, 138, 0.7);
  border-radius: 10px;
  box-shadow:
    0 0 0 1px rgba(0, 0, 0, 0.6),
    0 0 36px rgba(140, 230, 110, 0.35),
    0 18px 40px rgba(0, 0, 0, 0.6);
  left: 57.5%;
  overflow: hidden;
  pointer-events: none;
  position: absolute;
  top: 14%;
  width: 39.5%;

  video {
    display: block;
    height: 100%;
    object-fit: cover;
    width: 100%;
  }
`;

const InAction = styled.span`
  align-items: center;
  background: rgba(6, 16, 23, 0.75);
  border-radius: 99px;
  color: #c8f2a8;
  display: flex;
  font-size: 12px;
  font-weight: 700;
  gap: 6px;
  left: 10px;
  letter-spacing: 0.08em;
  padding: 4px 10px;
  position: absolute;
  text-transform: uppercase;
  top: 10px;

  i {
    animation: ${pulse} 1.6s ease-in-out infinite;
    background: #a8e08a;
    border-radius: 50%;
    box-shadow: 0 0 8px #a8e08a;
    height: 7px;
    width: 7px;
  }

  @media (prefers-reduced-motion: reduce) {
    i {
      animation: none;
    }
  }
`;

const Dismiss = styled.button`
  align-items: center;
  background: rgba(6, 16, 23, 0.65);
  border: none;
  border-radius: 50%;
  color: ${({ theme }) => theme.colors.ice};
  cursor: pointer;
  display: flex;
  height: 36px;
  justify-content: center;
  position: absolute;
  right: 8px;
  top: 8px;
  width: 36px;

  &:hover,
  &:focus-visible {
    background: ${({ theme }) => theme.colors.navy300};
    outline: 2px solid ${({ theme }) => theme.colors.ice};
  }
`;
